import memoize from 'memoize-one';

import { ActionNodeT } from 'fable/flowEditor';
import { AutomationFlowActionT, AutomationNodeTypes } from 'hocs';

export type NodeMap = {
  [x: string]: AutomationFlowActionT;
};

export const MIN_WAIT_TIME_BETWEEN_REPLIES = 7200; // 2 hours in seconds

const getParentNode = (
  actions: NodeMap,
  action: AutomationFlowActionT | ActionNodeT,
) => (action.parentId !== null ? actions[action.parentId] || null : null);

const getWaitTimeBetweenReplies = (
  actions: NodeMap,
  action: AutomationFlowActionT | ActionNodeT,
): number | null => {
  // return null if there is no "inbetween replies"
  // otherwise recursively return WaitTime
  const parent = getParentNode(actions, action);
  if (!parent) return null;
  const parentWaitTime =
    parent.nodeType === AutomationNodeTypes.WAIT
      ? parent.payload.delayInSeconds
      : 0;

  if (parent.nodeType === AutomationNodeTypes.INIT) {
    return null;
  }

  if (parent.nodeType === AutomationNodeTypes.REPLY) {
    return 0;
  }

  const parentAncestralWaitTime = getWaitTimeBetweenReplies(actions, parent);
  if (typeof parentAncestralWaitTime === 'number') {
    return parentWaitTime + parentAncestralWaitTime;
  }

  return null;
};

export const getAutoReplyValidity = memoize((actions: NodeMap) =>
  memoize((action: AutomationFlowActionT | ActionNodeT) => {
    const waitTimeBetweenReplies = getWaitTimeBetweenReplies(actions, action);
    const isInvalid =
      waitTimeBetweenReplies !== null &&
      waitTimeBetweenReplies < MIN_WAIT_TIME_BETWEEN_REPLIES;

    return {
      isInvalid,
      waitTime: waitTimeBetweenReplies,
      action,
    };
  }),
);

export const getInvalidReplies = memoize((actions: AutomationFlowActionT[]) => {
  const actionsMap = actions.reduce<NodeMap>((pre, cur) => {
    pre[cur.id] = cur;
    return pre;
  }, {} as NodeMap);
  const autoReplies = actions.filter(
    (act) => act.nodeType === AutomationNodeTypes.REPLY,
  );
  const invalidAutoReplies = autoReplies
    .map(getAutoReplyValidity(actionsMap))
    .filter((validity) => validity.isInvalid);
  return invalidAutoReplies.map((replyValidity) => replyValidity.action.id);
});
